import React, { useState } from "react";
import { ActionModal, ActionTarget } from "./ActionModal";
import {
  Action,
  Room,
  RoomDay,
  Task,
  actionBody,
  chipInk,
  whiteBadgeFill,
  taskBucket,
  roomDoorOrder,
  localInput,
  utcInstant,
  displayTime,
  declineLabel,
} from "./api";
export function TaskTable({
  rooms,
  act,
  settingsVersion,
  whiteBadges,
  compact = false,
  timezone = "Asia/Bangkok",
}: {
  rooms: Room[];
  settingsVersion: number;
  whiteBadges?: boolean;
  compact?: boolean;
  act: (room: Room, body: Action) => Promise<void>;
  timezone?: string;
}) {
  const [selected, select] = useState<ActionTarget | null>(null);
  const [reason, setReason] = useState(""),
    [initials, setInitials] = useState(""),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  const [occurredAt, setOccurredAt] = useState(
    localInput(new Date(), timezone),
  );
  const [pending, setPending] = useState<Action | null>(null);
  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!selected || busy) return;
    setBusy(true);
    setError("");
    const body =
      pending ||
      actionBody(
        selected.room,
        selected.task,
        selected.type,
        reason,
        initials,
        utcInstant(occurredAt, timezone),
        settingsVersion,
      );
    setPending(body);
    try {
      await act(selected.room, body);
      select(null);
      setPending(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  function open(room: Room, day: RoomDay, task: Task, type: string) {
    select({ room, day, task, type });
    setReason("");
    setInitials("");
    setError("");
    setPending(null);
    setOccurredAt(localInput(new Date(), timezone));
  }
  return (
    <>
      <div className="table-responsive">
        <table className="table table-sm table-hover align-middle">
          <thead>
            <tr>
              <th>Room</th>
              {compact ? (
                <>
                  <th>Clean</th>
                  <th>Details</th>
                  <th>Reservation</th>
                  <th>Time</th>
                  <th>Actions</th>
                </>
              ) : (
                <>
                  <th>Type</th>
                  <th>Guest</th>
                  <th>Stay day</th>
                  <th>Clean required</th>
                  <th>Status</th>
                  <th>Initials / notes</th>
                  <th>Record result</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {roomDoorOrder(rooms).flatMap((room) =>
              room.days.map((day) => (
                <React.Fragment key={`${room.roomId}-${day.date}`}>
                  {day.tasks.length === 0 && (
                    <tr>
                      <th>{room.roomLabel}</th>
                      <td>{room.groupLabel}</td>
                      <td>{day.guestNames.join(", ")}</td>
                      <td>{day.stayDay ?? day.occupancy}</td>
                      <td colSpan={4}>
                        No outstanding task · Readiness: {day.readiness}
                      </td>
                    </tr>
                  )}
                  {day.tasks.map((task) => (
                    <tr key={task.taskId}>
                      <th>{room.roomLabel}</th>
                      {compact ? (
                        <>
                          <td>
                            <span
                              className="hk-clean-chip"
                              style={{
                                backgroundColor: whiteBadgeFill(task.color),
                                color: "#fff",
                              }}
                            >
                              {task.code}
                            </span>
                          </td>
                          <td>
                            {task.state === "declined"
                              ? `${declineLabel(day)} · carries to tomorrow`
                              : task.triggerReason}
                            <small className="d-block text-body-secondary">
                              {task.windowStart || task.windowEnd
                                ? ` Work ${displayTime(task.windowStart, timezone)}${task.windowEnd ? ` to ${displayTime(task.windowEnd, timezone)}` : ""}`
                                : ""}
                            </small>
                          </td>
                          <td>
                            {task.reservationId ? (
                              <a
                                href={`/?reservationId=${encodeURIComponent(task.reservationId)}`}
                              >
                                {task.reservationNumber || "Reservation"}
                              </a>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td className="text-body-secondary text-nowrap">
                            {task.minutes}m
                          </td>
                        </>
                      ) : (
                        <>
                          <td>{room.groupLabel}</td>
                          <td>{day.guestNames.join(", ")}</td>
                          <td>{day.stayDay ?? day.occupancy}</td>
                          <td>
                            <span
                              className="badge"
                              style={{
                                backgroundColor: whiteBadges
                                  ? whiteBadgeFill(task.color)
                                  : task.color,
                                color: whiteBadges
                                  ? "#ffffff"
                                  : chipInk(task.color),
                              }}
                            >
                              {task.code}
                            </span>{" "}
                            {task.minutes} min
                            <details>
                              <summary className="small">Details</summary>
                              <p className="small mb-1">{task.description}</p>
                              <p className="small mb-1">
                                {task.triggerReason}{" "}
                                {displayTime(task.windowStart, timezone)}{" "}
                                {task.windowEnd &&
                                  `to ${displayTime(task.windowEnd, timezone)}`}
                              </p>
                              <small>
                                Due {task.originalDueDate} · Room readiness:{" "}
                                {day.readiness}
                                <br />
                                Reference: {task.taskId}
                              </small>
                            </details>
                          </td>
                          <td>
                            {taskBucket(task) === "overdue" && (
                              <span className="badge text-bg-danger me-1">
                                Overdue
                              </span>
                            )}
                            {
                              (
                                {
                                  todo: "To do",
                                  declined: "Declined / still due",
                                  completed: "Done",
                                  superseded: "Combined into another clean",
                                } as const
                              )[task.state]
                            }
                          </td>
                          <td>
                            {task.lastActionStaffInitials}
                            <small className="d-block">
                              {task.lastActionReason}
                            </small>
                          </td>
                        </>
                      )}
                      <td>
                        <div className="d-flex gap-1 flex-wrap">
                          {task.allowedActions.map((type) => (
                            <button
                              key={type}
                              className={
                                compact
                                  ? "hk-action-circle"
                                  : "btn btn-sm btn-outline-contrast"
                              }
                              aria-label={`${type === "complete" ? "Mark done" : type === "decline" ? declineLabel(day) : task.state === "declined" ? "Undo decline" : "Reopen"} ${room.roomLabel} ${task.code}`}
                              title={
                                type === "complete"
                                  ? "Mark done"
                                  : type === "decline"
                                    ? declineLabel(day)
                                    : task.state === "declined"
                                      ? "Undo decline"
                                      : "Reopen"
                              }
                              onClick={() => open(room, day, task, type)}
                            >
                              {compact
                                ? type === "complete"
                                  ? "✓"
                                  : type === "decline"
                                    ? "×"
                                    : "↶"
                                : type === "complete"
                                  ? "Done"
                                  : type === "decline"
                                    ? "Decline"
                                    : "Reopen"}
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              )),
            )}
            {!rooms.length && (
              <tr>
                <td colSpan={8}>Nothing here today.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {selected && (
        <ActionModal
          target={selected}
          timezone={timezone}
          initials={initials}
          occurredAt={occurredAt}
          reason={reason}
          error={error}
          busy={busy}
          pending={!!pending}
          onInitials={setInitials}
          onOccurredAt={setOccurredAt}
          onReason={setReason}
          onSubmit={submit}
          onClose={() => select(null)}
        />
      )}
    </>
  );
}
